import { SHOT_OUTCOMES, SHOT_OUTCOME_LABELS } from '@golf/common';
import type { ShotOutcome } from '@golf/common';
import { Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../../lib/auth.tsx';
import { useSettings } from '../../lib/settings.tsx';
import type { Shot } from '../../lib/training.ts';
import { fromMetres, panelMarks, toMetres, unitLabel } from '../../lib/units.ts';
import { ShotIcon } from './ShotIcon.tsx';

interface Props {
    shot: Shot;
    /** Rang affiché, pour que le joueur sache quelle balle il corrige. */
    position: number;
    busy: boolean;
    onSave: (patch: { outcome?: ShotOutcome; distanceM?: number | null }) => void;
    onDelete: () => void;
    onClose: () => void;
}

/** Correction d'une balle déjà saisie.
 *
 *  La saisie 1-Tap se fait en regardant sa balle, pas son écran : le pouce
 *  glisse, « Réussi » part à la place de « Top ». Sans moyen de corriger, la
 *  statistique reste fausse et le joueur le sait — ce qui suffit à lui faire
 *  abandonner la saisie en cours de séance.
 *
 *  L'erreur se remarque souvent une ou deux balles plus tard : n'importe
 *  laquelle s'ouvre, pas seulement la dernière. */
export function ShotEditor({ shot, position, busy, onSave, onDelete, onClose }: Props) {
    const { user } = useAuth();
    const { unit } = useSettings();
    const mirror = user?.dominantHand === 'left';

    const [outcome, setOutcome] = useState<ShotOutcome>(shot.outcome);
    const [distance, setDistance] = useState<string>(
        shot.distanceM != null ? String(fromMetres(shot.distanceM, unit)) : '',
    );

    const save = (): void => {
        onSave({
            outcome,
            distanceM: outcome === 'center' && distance.trim() !== ''
                ? toMetres(Number(distance), unit)
                : null,
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center">
            <div className="max-h-[92dvh] w-full max-w-lg overflow-y-auto rounded-t-box bg-base-200 sm:rounded-box">
                <div className="sticky top-0 flex items-center justify-between bg-base-200 px-4 py-3">
                    <h2 className="text-lg font-bold">Corriger la balle {position}</h2>
                    <button type="button" className="btn btn-ghost btn-square" onClick={onClose}
                        aria-label="Fermer">
                        <X size={20} />
                    </button>
                </div>

                <div className="space-y-4 px-4 pb-4">
                    <div>
                        <span className="mb-2 block text-sm font-medium">Ce qu'a fait la balle</span>
                        <div className="grid grid-cols-4 gap-2">
                            {SHOT_OUTCOMES.map((o) => (
                                <button
                                    key={o}
                                    type="button"
                                    onClick={() => setOutcome(o)}
                                    aria-pressed={outcome === o}
                                    className={`btn-outcome h-16 !text-xs ${
                                        outcome === o
                                            ? '!bg-primary !text-primary-content !border-primary'
                                            : ''
                                    }`}
                                >
                                    <ShotIcon outcome={o} mirror={mirror} size={24} />
                                    <span className="leading-none">{SHOT_OUTCOME_LABELS[o]}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* La distance ne survit pas au passage à une issue manquée :
                        le champ disparaît, et le serveur l'efface. */}
                    {outcome === 'center' && (
                        <label className="block">
                            <span className="mb-1 block text-sm font-medium">
                                Distance ({unitLabel(unit)})
                            </span>
                            <input
                                type="number"
                                inputMode="numeric"
                                min={1}
                                max={450}
                                className="input input-bordered w-full text-lg tabular-nums"
                                value={distance}
                                placeholder="sans mesure"
                                onChange={(e) => setDistance(e.target.value)}
                            />
                            <span className="mt-1 block text-xs opacity-60">
                                Laissez vide si la balle n'a pas été mesurée.
                            </span>
                        </label>
                    )}

                    {/* Les mêmes panneaux qu'à la saisie, pour la même raison :
                        une distance corrigée vient d'une pancarte relue, pas
                        d'un calcul. Ici ils REMPLISSENT le champ sans
                        enregistrer — on est venu revoir l'issue autant que la
                        distance, et fermer la fenêtre au premier appui
                        emporterait la correction qu'on n'a pas encore faite. */}
                    {outcome === 'center' && (
                        <div className="grid grid-cols-4 gap-2">
                            {panelMarks(unit).map((v) => (
                                <button
                                    key={v}
                                    type="button"
                                    onClick={() => setDistance(String(v))}
                                    aria-pressed={distance === String(v)}
                                    className={`h-11 rounded-field text-sm font-semibold tabular-nums transition-colors ${
                                        distance === String(v)
                                            ? 'bg-primary text-primary-content'
                                            : 'bg-base-100 hover:bg-base-300'
                                    }`}
                                >
                                    {v}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="flex gap-2 pt-1">
                        <button type="button" disabled={busy} onClick={onDelete}
                            className="btn btn-outline btn-error gap-2">
                            <Trash2 size={18} /> Supprimer
                        </button>
                        <button type="button" disabled={busy} onClick={save}
                            className="btn btn-primary flex-1 tap-target">
                            Enregistrer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
